import { useCallback, useMemo } from "react";
import { useQuery, type QueryClient, type RefetchOptions, type QueryObserverResult } from "@tanstack/react-query";
import {
  getCityhallStateCityhallStateGet,
  type CandidateInfo,
  type CityHallStateResponse,
} from "../../client";
import type { UserData } from "../../models/user";
import { getMapObjects, type MapObject } from "../../models/mapObject";
import { createRefetchIntervalFunction } from "../../utils/net/apiError";
import useInterfaceStore from "../../stores/useInterfaceStore";

export interface UseCityHallDataParams {
  mapObjectId: number | undefined;
  onClose: (() => void) | undefined;
  queryClient: QueryClient;
}

export interface UseCityHallDataResult {
  currentLeader: CandidateInfo | null;
  freePlots: MapObject[];
  handleEnterInterior: () => void;
  isLoadingState: boolean;
  refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<CityHallStateResponse, Error>>;
  sortedCandidates: CandidateInfo[];
  state: CityHallStateResponse | undefined;
  userData: UserData | undefined;
}

export function useCityHallData({
  mapObjectId,
  onClose,
  queryClient,
}: UseCityHallDataParams): UseCityHallDataResult {
  const { data: state, refetch, isLoading: isLoadingState } = useQuery({
    queryKey: ['cityhall-state'],
    queryFn: async () => {
      const { data } = await getCityhallStateCityhallStateGet({ throwOnError: true });
      return data;
    },
    enabled: true,
    refetchInterval: createRefetchIntervalFunction(30000),
  });

  const userData = queryClient.getQueryData<UserData>(['userData']);

  // Free plots: unowned plot map-objects (used by the "Free plots" metric tile + its
  // "Go to a free plot" popup action, which reuses MapCanvas's plot deep-link focus).
  // Always subscribe — even when City Hall opened with mapObjectId (map modal path),
  // otherwise freePlots stays [] and the chart action never appears.
  const { data: mapObjectsData } = useQuery<MapObject[]>({
    queryKey: ['mapObjects'],
    queryFn: getMapObjects,
    staleTime: Infinity, // reuse MapCanvas cache — don't refetch
  });
  const cityhallMapObjectId = useMemo(() => {
    if (mapObjectId) return mapObjectId;
    return mapObjectsData?.find(o => o.type === 'cityhall')?.id;
  }, [mapObjectId, mapObjectsData]);

  // Free plots: unowned plot map-objects (used by the "Free plots" metric tile + its
  // "Go to a free plot" popup action, which reuses MapCanvas's plot deep-link focus).
  const freePlots = useMemo(
    () => (mapObjectsData ?? []).filter(o => o.type === 'plot' && o.owner_id === null),
    [mapObjectsData]
  );

  const handleEnterInterior = useCallback(() => {
    if (!cityhallMapObjectId) return;
    if (onClose) onClose();
    useInterfaceStore.getState().setRoomView(
      cityhallMapObjectId,
      state?.mayor?.user_id ?? null
    );
  }, [cityhallMapObjectId, onClose, state?.mayor?.user_id]);

  // Candidates ordered by registration number (id) — never re-sorted
  // Dep on `state` (not state?.candidates): React Compiler needs a stable base path.
  const sortedCandidates = useMemo(() => {
    if (!state?.candidates) return [];
    return [...state.candidates].sort((a, b) => a.id - b.id);
  }, [state]);

  // Current leader — always the candidate with the most votes (dynamic, not locked)
  const currentLeader = useMemo(() => {
    if (!state?.candidates?.length) return null;
    return [...state.candidates].sort((a, b) => b.votes_count - a.votes_count)[0];
  }, [state]);

  return {
    currentLeader,
    freePlots,
    handleEnterInterior,
    isLoadingState,
    refetch,
    sortedCandidates,
    state,
    userData,
  };
}
